/*
 * Copyright © 2020-2023 Ocean Galaxy Inc. All Rights Reserved.
 * @Description:
 * @LastEditors: yingongjing
 * @LastEditTime: 2025-10-20 17:36:52
 */
import './index.scss'
import { Button, Layout, Modal, message } from 'antd'
import classNames from 'classnames'
import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAppDispatch, useAppSelector } from '../../../app/hooks'
import Filter from './Filter'
import PhotosList from './PhotosList'
import { fetchPhotos, setQueryFilter } from '../../../actions/photo'
import actionTypes from '../../../constants/actionTypes'
import CONFIG from '../../../constants/config'
import { capturePromiseException } from '../../../utils/sentryUtils'
import sensors, { sensorsRole } from '../../../utils/sensors'
import parseQueryString from '../../../utils/parseQueryString'
import {
  setBackFromPickerPage,
  setPhotoPickerPhotos
} from '../../../actions/photoPicker'
import { buildFetchPhotosParams } from '@/pages/components/photoLib/photo'
import { LeftOutlined } from '@ant-design/icons'

import PageHeader from '@/components/PageHeader'
const { Content } = Layout

/**
 * pageTitle: 页面标题
 * from: 从哪个页面跳转过来的
 * mediaType: 是否只能选择照片（有视频）
 * sourceOnly: 是否只能选择今拍
 * max: 最多可选多少张，0为不限制
 */

const PhotoPickerPage = () => {
  const navigate = useNavigate()
  const { pageTitle, from, mediaType, sourceOnly, max, action } =
    parseQueryString()
  const title = pageTitle ? decodeURI(pageTitle) : '选择照片'
  const filter = useAppSelector((state) => state.photo.filter)

  const groupID = useAppSelector((state) => state.group.groupId)
  const userId = useAppSelector((state) => state.main.user?.user_id)
  const myRole = useAppSelector((state) => state.group.myRole)
  const checkedPhotos = useAppSelector((state) => state.photo.checkedPhotos)
  const unCheckedPhotos = useAppSelector((state) => state.photo.unCheckedPhotos)
  const photos = useAppSelector((state) => state.photo.photos)
  const photoState = useAppSelector((state) => state.photo)
  const checkAll = photoState.checkAll

  const dispatch = useAppDispatch()
  const photoListRef = useRef()

  // AI 安全页面的多选状态管理
  const [aiSecurityMultiSelect, setAiSecurityMultiSelect] = useState(false)

  const isFromJigsaw = useMemo(() => {
    return from === 'jigsaw'
  }, [from])

  const isFromAiSecurity = useMemo(() => {
    return from && from.includes('ai-security')
  }, [from])

  const multipleSelect = useMemo(() => {
    // AI 安全页面使用独立的多选状态
    if (isFromAiSecurity) {
      return aiSecurityMultiSelect
    }
    // 其他页面继续使用 max 参数判断
    return max && parseInt(max) > 1
  }, [max, isFromAiSecurity, aiSecurityMultiSelect])

  const words = useMemo(() => {
    if (from === 'jigsaw') {
      return {
        subTitle: '请先选择要拼图的照片「支持筛选照片和点选照片」',
        submitBtnText: '开始拼图'
      }
    }
    if (from === 'rectify') {
      return {
        subTitle: '请选择整改照片「支持筛选照片和点选照片」',
        submitBtnText: '确认选择'
      }
    }
    if (from && from.includes('ai-security')) {
      return {
        subTitle: '请先选择要识别的照片「支持筛选照片和点选照片」',
        submitBtnText: '开始识别'
      }
    }
    if (from === 'mingyangAi') {
      return {
        subTitle: '请选择需要 AI 分析的照片「支持筛选照片和点选照片」',
        submitBtnText: '开始智能分析'
      }
    }
    return ''
  }, [from])

  const checkedCount = useMemo(() => {
    return checkAll
      ? photos.length - Object.keys(unCheckedPhotos || {}).length
      : Object.keys(checkedPhotos || {}).length
  }, [checkedPhotos, unCheckedPhotos, checkAll, photos])

  const overPickLimit = useMemo(() => {
    if (max && parseInt(max) > 0) {
      return checkedCount > parseInt(max)
    }
    return false
  }, [checkedCount, max])

  // const submitDisabled = useMemo(() => {
  //   return from !== 'jigsaw' && (checkedCount === 0 || overPickLimit)
  // }, [checkedCount, overPickLimit, from])

  const handleCancelChecked = () => {
    dispatch({ type: actionTypes.CLEAR_CHECKED_PHOTO })
    dispatch({ type: actionTypes.CLEAR_UNCHECKED_PHOTO })
    dispatch({ type: actionTypes.SET_CHECK_ALL, data: false })
  }

  useEffect(() => {
    const groupId = localStorage.getItem('group_id')

    if (!groupId) {
      return navigate('/choose_group', { replace: true })
    }

    if (myRole !== CONFIG.memberRoleTypes.ALL) {
      dispatch(setQueryFilter(filter))
      dispatch(fetchPhotos(buildFetchPhotosParams(filter, groupId, ''))).catch(
        capturePromiseException
      )
    }
  }, [myRole])

  useEffect(() => {
    sensors.track('web_workgroup_common_pick_photos_entry', {
      from,
      mediaType,
      sourceOnly,
      max
    })
    return () => {
      //重置查询条件和数据
      dispatch({ type: actionTypes.RESET_FILTER })
      dispatch({ type: actionTypes.SET_PHOTOS, data: [] })
      dispatch({ type: actionTypes.SET_PHOTOS_NUM, data: 0 })
      dispatch({ type: actionTypes.CLEAR_CHECKED_PHOTO })
      dispatch({ type: actionTypes.CLEAR_UNCHECKED_PHOTO })
      dispatch({ type: actionTypes.SET_CHECK_ALL, data: false })
    }
  }, [])

  const handleSensorOfClickEvent = (
    clickItem: string,
    restProperties?: Record<string, any>
  ) => {
    sensors.track('web_workgroup_common_pick_photos_click', {
      clickItem,
      from: from === 'rectify' ? '发起整改页面' : from,
      mediaType,
      sourceOnly,
      max,
      action,
      role: sensorsRole(myRole),
      track_sign:
        'aworkgroup.b4393.action.web_workgroup_common_pick_photos_click',
      requirement_id: 156,
      ...restProperties
    })
  }

  const handleSubmit = () => {
    handleSensorOfClickEvent('finish', {
      num: checkedCount
    })

    let photoIDs: string[] = []

    if (checkAll) {
      //全选为已加载出的照片
      const all = photos.map((item: any) => String(item.seq))
      const unchecked = Object.keys(unCheckedPhotos || {})

      photoIDs = all.filter((item: string) => !unchecked.includes(item))
    } else {
      photoIDs = Object.keys(checkedPhotos || {})
    }

    if (checkedCount === 0 && isFromJigsaw) {
      Modal.info({
        title: '请先选择照片',
        content: '选择照片后，点击"开始拼图"按钮'
      })
      return
    }

    const selectedPhotos = photos.filter((item: any) =>
      photoIDs.includes(String(item.seq))
    )
    dispatch(setPhotoPickerPhotos(selectedPhotos, from as any))
    dispatch(setBackFromPickerPage(true))
    navigate(-1)
  }

  const handleOnePickSubmit = (photo: any) => {
    if (photo.mediaType === 1) {
      message.error('暂不支持视频识别')
      return
    }

    // 单选跳转AI安全漏洞识别埋点
    handleSensorOfClickEvent('onePickSubmit', {
      from: from
    })

    dispatch(setPhotoPickerPhotos([photo], from))
    if (from === 'all-photo-ai-security-banner') {
      navigate(encodeURI(`/ai/security`))
    } else if (from === 'ai-security') {
      navigate(-1)
    } else {
      navigate(-1)
    }
  }

  return (
    <Layout className={classNames('Photo', 'photo-picker-page')}>
      <PageHeader
        ghost={false}
        style={{
          borderBottom: '1px solid rgb(235, 237, 240)',
          padding: '8px 16px'
        }}
        title={
          <div className="title">
            <LeftOutlined
              style={{ marginRight: 8 }}
              onClick={() => {
                handleSensorOfClickEvent('back')
                dispatch(setBackFromPickerPage(true))
                dispatch(setPhotoPickerPhotos([], null as any))
                navigate(-1)
              }}
            />
            {title}
            <div className="tip">
              {typeof words === 'object' ? words.subTitle : ''}
            </div>
            {max && overPickLimit && (
              <span style={{ color: 'red', marginLeft: 10 }}>
                最多可选择{max}张照片！
              </span>
            )}
          </div>
        }
      />
      <Content className="content">
        <Filter
          words={words}
          multipleSelect={multipleSelect}
          photoListRef={photoListRef}
          handleSensorOfClickEvent={handleSensorOfClickEvent}
          onStartJigsaw={handleSubmit}
          startJigsawDisable={max && overPickLimit}
        />

        <PhotosList
          from={from}
          multipleSelect={multipleSelect}
          isFromAiSecurity={isFromAiSecurity}
          aiSecurityMultiSelect={aiSecurityMultiSelect}
          setAiSecurityMultiSelect={setAiSecurityMultiSelect}
          handleOnePickSubmit={handleOnePickSubmit}
          photoListRef={photoListRef}
          maxSelectNum={max}
          handleSensorOfClickEvent={handleSensorOfClickEvent}
        />
      </Content>
    </Layout>
  )
}

export default PhotoPickerPage
